import { GROSS_UNIT, UNIT } from 'src/settings/settings';
import { ActionType, createReducer } from 'typesafe-actions';
import * as SellActions from './actions';
import { SellTransactionActionTypes as act } from './actionTypes';
import { SellFund, SellTransactionState, SellUnitSelection } from './types';
export * from './actions';

export const initialState: SellTransactionState = {
  sellLookup: {},
  selectedBank: undefined,
  rmdShouldValidate: true,
};

export const sellTransactionReducer = createReducer<
  SellTransactionState,
  ActionType<typeof SellActions>
>(initialState)
  .handleAction(
    [act.SELECT_SELL_FUND, act.DESELECT_SELL_FUND, act.UPDATE_SELL_FUND],
    (state, { payload, type }) => {
      const { sellLookup } = state;
      const { agreementID = '', ...restOfPayload } = payload;
      const newCartItem = {
        ...(sellLookup[agreementID] || {
          [GROSS_UNIT]: UNIT.DOLLARS,
        }),
      };
      newCartItem.selected = type !== act.DESELECT_SELL_FUND;

      if (type === act.UPDATE_SELL_FUND) {
        Object.assign(newCartItem, restOfPayload);
      }
      return {
        ...state,
        sellLookup: {
          ...sellLookup,
          [agreementID]: newCartItem,
        },
      };
    }
  )
  .handleAction(
    act.DISTRIBUTION_BENEFICIARY_ADDRESS_FULFILLED,
    (state, { payload }) => {
      const { sellLookup } = state;
      const { body } = payload;

      /**
       * Parses the addressPayload.addressLines[] into {street[1,2,3...]: addressLine}
       * @returns sellLookup[agreementID].mailCheckBeneficiaryAddress
       * */

      const returnObj = Object.entries(body).reduce<
        Record<SellFund['agreementID'], SellUnitSelection>
      >((accumulator, [agreementID, addressPayload]) => {
        const newCartItem = sellLookup[agreementID] || {};
        const {
          addressLines,
          country,
          state,
          city,
          zipCode: zip,
          zipCodeExt,
          address: street1,
        } = addressPayload;

        const streetAddressLines = addressLines.reduce(
          (acc, addressLine, i) => ({
            ...acc,
            ['street' + (1 + i)]: addressLine,
          }),
          {}
        );

        // return the accumulated object
        return {
          ...accumulator,
          [agreementID]: {
            ...newCartItem,
            mailCheckBeneficiaryAddress: {
              street1,
              city,
              country,
              state,
              zipCodeExt,
              zip,
              ...streetAddressLines,
            },
          },
        };
      }, {});

      return {
        ...state,
        sellLookup: {
          ...sellLookup,
          ...returnObj,
        },
      };
    }
  )
  .handleAction(act.UPDATE_MONTH_SELECTION, (state, { payload }) => {
    const { selection, agreementID } = payload;
    const { sellLookup } = state;
    const selectedFund = { ...sellLookup?.[agreementID] } || {};
    return {
      ...state,
      sellLookup: {
        ...sellLookup,
        [agreementID]: { ...selectedFund, selectedMonths: selection },
      },
    };
  })
  .handleAction(act.UPDATE_DAY_SELECTION, (state, { payload }) => {
    const { selection, agreementID } = payload;
    const { sellLookup } = state;
    const selectedFund = { ...sellLookup?.[agreementID] } || {};
    return {
      ...state,
      sellLookup: {
        ...sellLookup,
        [agreementID]: { ...selectedFund, selectedDays: selection },
      },
    };
  })
  .handleAction(act.UPDATE_SELL_UNIQUE_IDS, (state, { payload }) => {
    const { sellLookup } = state;
    const {
      agreementID,
      electionAgreementUniqueId1,
      electionAgreementUniqueId2,
    } = payload;
    const selectedFund = { ...sellLookup?.[agreementID] } || {};
    return {
      ...state,
      sellLookup: {
        ...sellLookup,
        [agreementID]: {
          ...selectedFund,
          electionAgreementUniqueId1,
          electionAgreementUniqueId2,
        },
      },
    };
  })
  .handleAction(act.RESET_TRANSACTION, (state) => {
    return {
      ...state,
      sellLookup: {},
      selectedBank: undefined,
    };
  })
  .handleAction(act.UPDATE_FEDERAL_WITHHOLDING, (state, { payload }) => {
    const { sellLookup } = state;
    const {
      federalWithholdingApplied,
      federalWithholdingPercent,
      agreementID,
    } = payload;

    // make sure to destructure so we get a copy
    const selectedFund = { ...sellLookup?.[agreementID] } || {};

    // reassemble all the pieces
    return {
      ...state,
      sellLookup: {
        ...sellLookup,
        [agreementID]: {
          ...selectedFund,
          federalWithholdingApplied,
          federalWithholdingPercent,
        },
      },
    };
  });

export default sellTransactionReducer;
